import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PairingCode } from '@cloudsign/shared';
import type { DeviceKind } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useSite } from '../../lib/site';
import { Button, Card, SelectField, TextField } from '../../components/ui';
import { ErrorLine, fieldErrors } from '../../components/ErrorPanel';
import { BASE, DEVICE_KINDS } from './api';

/** "1234567" -> "123 4567", so it is easy to read out and type. */
export const spaced = (code: string): string => `${code.slice(0, 3)} ${code.slice(3)}`;

/** Asks for a 7-digit pairing code for a new device and shows it once. */
export function PairCard({ onDone }: { onDone: () => void }) {
  const { sites, siteId } = useSite();
  const [site, setSite] = useState(siteId ?? '');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<DeviceKind>('kiosk');
  const chosenSite = site || sites[0]?.id || '';
  const create = useMutation({
    mutationFn: () => api.post(`${BASE}/pairing-codes`, { body: { siteId: chosenSite, name, kind }, schema: PairingCode }),
  });
  const errs = fieldErrors(create.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  if (create.data) {
    return (
      <Card title={t('devicesUi.pair.codeTitle')} className="mb-6">
        <p className="text-3xl font-bold tracking-widest text-slate-900" aria-label={t('devicesUi.pair.codeLabel', { code: create.data.code.split('').join(' ') })}>
          <span aria-hidden="true">{spaced(create.data.code)}</span>
        </p>
        <p className="mt-2 text-sm text-slate-600">{t('devicesUi.pair.expires', { time: formatDateTime(create.data.expiresAt) })}</p>
        <div className="mt-4">
          <Button variant="primary" onClick={onDone}>
            {t('devicesUi.pair.done')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title={t('devicesUi.pair.title')} description={t('devicesUi.pair.description')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-3">
          <ErrorLine error={create.error} />
        </div>
        <SelectField label={t('devicesUi.pair.site')} value={chosenSite} onChange={(e) => setSite(e.target.value)} error={errs['siteId']}>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <TextField label={t('devicesUi.pair.name')} required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} error={errs['name']} />
        <SelectField label={t('devicesUi.pair.kind')} value={kind} onChange={(e) => setKind(e.target.value as DeviceKind)}>
          {DEVICE_KINDS.map((k) => (
            <option key={k} value={k}>
              {t(`devicesUi.kind.${k}`)}
            </option>
          ))}
        </SelectField>
        <div className="flex items-end gap-2 sm:col-span-3">
          <Button type="submit" variant="primary" disabled={create.isPending || name.trim() === '' || !chosenSite}>
            {t('devicesUi.pair.submit')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}
